import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Globe } from 'lucide-react';
import { useI18n } from '../../../../hooks/useInternationalization';
import CONFIG from '../../../../config';
import './LanguageSwitcher.css';

const LanguageSwitcher = () => {
  const { language, changeLanguage, country } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Compute Irish English display label based on user's country
  const getIrishEnglishLabel = () => {
    if (country === 'GB') {
      return 'English (UK IE)';
    }
    return 'English (Ireland)';
  };

  const irishEnglishLabel = getIrishEnglishLabel();

  // Define available languages based on feature flag (no flags shown)
  const allLanguages = [
    { code: 'en-IE', name: irishEnglishLabel },
    { code: 'en-US', name: 'English (US)' },
    { code: 'ga', name: 'Gaeilge' },
    { code: 'fr', name: 'Français' },
    { code: 'de', name: 'Deutsch' },
    { code: 'es', name: 'Español' }
  ];

  // Limit to Irish-focused languages if feature flag is disabled
  const languages = CONFIG.features.enableAllLanguages 
    ? allLanguages
    : [
        { code: 'en-IE', name: irishEnglishLabel },
        { code: 'en-US', name: 'English (US)' },
        { code: 'ga', name: 'Gaeilge' }
      ];

  const currentLanguage = languages.find(lang => lang.code === language) || languages[0];

  // Handle clicks outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = async (langCode) => {
    if (langCode !== language) {
      await changeLanguage(langCode);
    }
    setIsOpen(false);
  };

  return (
    <div className="language-switcher" ref={dropdownRef}>
      <button
        className="language-trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Change language"
      >
        <Globe size={18} />
        <span className="current-language">
          <span className="name">{currentLanguage.name}</span>
        </span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronDown size={16} />
        </motion.div>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="language-dropdown"
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
          >
            {languages.map((lang) => (
              <button
                key={lang.code}
                className={`language-option ${lang.code === language ? 'active' : ''}`}
                onClick={() => handleLanguageChange(lang.code)}
              >
                <span className="name">{lang.name}</span>
                {lang.code === language && (
                  <motion.div
                    className="check-mark"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  >
                    ✓
                  </motion.div>
                )}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LanguageSwitcher;
